import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Common.css';

import product1 from '../assets/images/product1.jpg';
import product2 from '../assets/images/product2.jpg';
import product3 from '../assets/images/product3.jpeg';

import { apiService } from '../api/api';

const BASE_URL = 'https://uatapi.ecommerce.ashtamtechnologies.com/api/';
const BASE_URL1 = 'https://uatapi.ecommerce.ashtamtechnologies.com/';

/* ─────────────────────────────────────────────────────────────────
   Resolve any image URL (relative or absolute) to a full https URL
───────────────────────────────────────────────────────────────── */
const resolveImageUrl = (raw, fallback) => {
  if (!raw) return fallback;
  if (raw.includes('default-product.png') || raw.includes('default.png')) return fallback;
  if (raw.startsWith('http://')) return raw.replace('http://', 'https://');
  if (raw.startsWith('https://')) return raw;
  // relative path e.g. /uploads/product-images/xxx.png
  return `${BASE_URL1}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

/* ─────────────────────────────────────────────────────────────────
   Safely extract the real numeric product ID from a raw API object.
   ONLY called on raw API objects (originalData), NOT on the mapped
   product where .id is a local index.
───────────────────────────────────────────────────────────────── */
const extractRawId = (obj) => {
  if (!obj) return null;
  // Try every field name the API might use
  for (const k of ['id', 'productId', 'ID', 'Id', 'ecommerceProductId', 'EcommerceProductId']) {
    const v = parseInt(obj[k]);
    if (!isNaN(v) && v > 0) return v;
  }
  return null;
};

/* ─────────────────────────────────────────────────────────────────
   Resolve the real DB product id from a mapped product object.

   PRIORITY ORDER — stops at the first valid positive integer:
     1. product.apiProductId   ← explicitly set during mapping
     2. product.originalData.* ← raw API object, try known field names
     3. null                   ← give up, show fallback image
───────────────────────────────────────────────────────────────── */
const resolveApiProductId = (product) => {
  if (!product) return null;

  // 1. apiProductId (set during getAllProductsForDisplay mapping)
  const v1 = parseInt(product.apiProductId);
  if (!isNaN(v1) && v1 > 0) return v1;

  // 2. originalData (raw API response object)
  const v2 = extractRawId(product.originalData);
  if (v2) return v2;

  return null;
};

/* ─────────────────────────────────────────────────────────────────
   CartStatusWidget
───────────────────────────────────────────────────────────────── */
const CartStatusWidget = ({ quantity, onIncrease, onDecrease, onRemove }) => {
  const [hovered, setHovered] = useState(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: '#f0fdf4', border: '1px solid #bbf7d0',
        color: '#16a34a', fontSize: '0.76rem', fontWeight: 700,
        padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap',
      }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        In Cart
      </span>

      <div style={{ display: 'inline-flex', alignItems: 'center', border: '1.5px solid #d1d5db', borderRadius: 8, overflow: 'hidden', height: 36, background: '#fff' }}>
        <button onClick={onDecrease} onMouseEnter={() => setHovered('dec')} onMouseLeave={() => setHovered(null)}
          style={{ width: 34, height: 36, border: 'none', background: hovered === 'dec' ? '#f9fafb' : '#fff', color: '#374151', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          −
        </button>
        <span style={{ minWidth: 34, textAlign: 'center', fontSize: '0.9rem', fontWeight: 700, color: '#111827', borderLeft: '1.5px solid #d1d5db', borderRight: '1.5px solid #d1d5db', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
          {quantity}
        </span>
        <button onClick={onIncrease} onMouseEnter={() => setHovered('inc')} onMouseLeave={() => setHovered(null)}
          style={{ width: 34, height: 36, border: 'none', background: hovered === 'inc' ? '#f9fafb' : '#fff', color: '#374151', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          +
        </button>
      </div>

      <button onClick={onRemove} onMouseEnter={() => setHovered('rem')} onMouseLeave={() => setHovered(null)}
        style={{ background: 'none', border: 'none', padding: '0 2px', color: hovered === 'rem' ? '#dc2626' : '#9ca3af', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: hovered === 'rem' ? '#dc2626' : '#d1d5db' }}>
        Remove
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ProductDetail
═══════════════════════════════════════════════════════════════ */
const ProductDetail = ({
  selectedProduct,
  onProductClick,
  onContactClick,
  onAddToCart,
  onUpdateQuantity,
  onRemoveFromCart,
  cartItems = [],
}) => {
  const fallbackImages = [product1, product2, product3];

  const [currentProduct, setCurrentProduct] = useState(null);
  const [allProducts, setAllProducts]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [productImages, setProductImages]   = useState([]);
  const [selectedImage, setSelectedImage]   = useState(null);
  const [imagesLoading, setImagesLoading]   = useState(false);

  /* ── Cart helpers ── */
  const cartEntry = cartItems.find(item =>
    currentProduct?.apiProductId && item.apiProductId
      ? item.apiProductId === currentProduct.apiProductId
      : item.id === currentProduct?.id
  );
  const cartItemKey  = cartEntry?.cartItemId ?? cartEntry?.id ?? null;
  const cartQuantity = cartEntry?.quantity ?? 0;
  const isInCart     = cartQuantity > 0;

  const handleIncrease = () => { if (cartItemKey) onUpdateQuantity?.(cartItemKey, cartQuantity + 1); };
  const handleDecrease = () => {
    if (!cartItemKey) return;
    cartQuantity === 1 ? onRemoveFromCart?.(cartItemKey) : onUpdateQuantity?.(cartItemKey, cartQuantity - 1);
  };
  const handleRemove = () => { if (cartItemKey) onRemoveFromCart?.(cartItemKey); };

  /* ════════════════════════════════════════════════════════════════
     fetchProductImages
     Uses resolveApiProductId() which checks apiProductId FIRST,
     then falls back to originalData — never touches product.id
     (which is just a local list index, not the real DB id).
  ════════════════════════════════════════════════════════════════ */
  const fetchProductImages = async (product) => {
    if (!product) return;

    const numericId = resolveApiProductId(product);

    console.log(`📷 fetchProductImages — "${product.name}"`, {
      'product.id (local index)': product.id,
      'product.apiProductId':     product.apiProductId,
      'resolved numericId':       numericId,
    });

    if (!numericId) {
      console.warn('⚠️ No valid apiProductId. Raw originalData:', product.originalData);
      const fallback = product.image || fallbackImages[0];
      setProductImages([{ id: 0, imageUrl: fallback, isPrimary: true, displayOrder: 0 }]);
      setSelectedImage(fallback);
      return;
    }

    try {
      setImagesLoading(true);
      console.log(`🌐 GET /api/ProductImage/product/${numericId}`);

      // apiService.getProductImages → GET /ProductImage/product/{numericId}
      // The response interceptor unwraps response.data, so `data` IS the array:
      // [{ id, productId, imageUrl, isPrimary, displayOrder }, ...]
      const data = await apiService.getProductImages(numericId);
      console.log(`📦 Image API response (productId=${numericId}):`, data);

      if (Array.isArray(data) && data.length > 0) {
        const mapped = data
          .map(img => ({
            id:           img.id          ?? img.imageId ?? img.Id ?? img.ID,
            imageUrl:     resolveImageUrl(
                            img.imageUrl  ?? img.ImageUrl ?? img.url ?? img.Url ?? img.image,
                            product.image || fallbackImages[0]
                          ),
            isPrimary:    img.isPrimary    ?? img.IsPrimary    ?? false,
            displayOrder: img.displayOrder ?? img.DisplayOrder ?? 0,
          }))
          .sort((a, b) => a.displayOrder - b.displayOrder);

        console.log(`✅ ${mapped.length} image(s):`, mapped.map(i => i.imageUrl));
        setProductImages(mapped);
        const primary = mapped.find(i => i.isPrimary) ?? mapped[0];
        setSelectedImage(primary.imageUrl);

      } else {
        console.warn(`⚠️ No images returned for productId=${numericId}`);
        const fallback = product.image || fallbackImages[0];
        setProductImages([{ id: 0, imageUrl: fallback, isPrimary: true, displayOrder: 0 }]);
        setSelectedImage(fallback);
      }
    } catch (err) {
      console.error(`❌ getProductImages(${numericId}) failed:`, err?.response?.status, err?.message);
      const fallback = product.image || fallbackImages[0];
      setProductImages([{ id: 0, imageUrl: fallback, isPrimary: true, displayOrder: 0 }]);
      setSelectedImage(fallback);
    } finally {
      setImagesLoading(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     Fetch all products for "More products" section.

     KEY: we store the real DB id in `apiProductId` separately from
     `id` (which is just index + 1 used as a React key).
     The raw API's `p.id` IS the real DB id here because `p` is
     directly from GetAllProductDisplay, not a mapped object.
  ════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await apiService.getAllProductsForDisplay();

        let raw = [];
        if (Array.isArray(response))            raw = response;
        else if (Array.isArray(response?.data)) raw = response.data;
        else if (response?.message === 'Successfully' && response.data) raw = response.data;

        // Log first product so you can verify field names
        if (raw.length > 0) {
          console.log('🔍 First raw product from GetAllProductDisplay:', JSON.stringify(raw[0], null, 2));
        }

        setAllProducts(raw.map((p, index) => ({
          // ← local index for React keys / filtering. NOT the DB id.
          id:           index + 1,

          name:         p.name?.trim() || `Product ${index + 1}`,
          category:     p.category     || 'N/A',
          categoryName: p.category     || 'N/A',
          description:  p.description  || 'No description available.',
          salePrice:    p.salePrice    ?? p.SalePrice ?? null,
          image:        resolveImageUrl(p.imageUrl, fallbackImages[index % fallbackImages.length]),

          // ← the REAL numeric DB id from the API response
          apiProductId: extractRawId(p),

          // ← keep the full raw object so fetchProductImages can fall back to it
          originalData: p,
        })));
      } catch (err) {
        console.error('❌ Failed to fetch products:', err);
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  /* ── When selectedProduct changes → load its images ── */
  useEffect(() => {
    if (!selectedProduct) return;
    setCurrentProduct(selectedProduct);
    setProductImages([]);
    setSelectedImage(null);
    fetchProductImages(selectedProduct);
    window.scrollTo(0, 0);
  }, [selectedProduct]);

  /* ── Derived ── */
  const relatedProducts = currentProduct
    ? allProducts.filter(p => p.id !== currentProduct.id).sort(() => 0.5 - Math.random()).slice(0, 8)
    : [];

  const originalData    = currentProduct?.originalData || {};
  const hasLicense      = Boolean(originalData.license);
  const hasStateLicense = Boolean(originalData.statelicense);

  /* ── Handlers ── */
  const handleProductClick = (product) => {
    onProductClick?.(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGetQuote = () => {
    onContactClick?.();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddToCart = () => {
    if (!currentProduct) { alert('No product selected.'); return; }
    if (localStorage.getItem('isLoggedIn') !== 'true' || !localStorage.getItem('userId')) {
      alert('Please login to add items to cart'); return;
    }
    const apiProductId = currentProduct.apiProductId || currentProduct.id;
    if (!apiProductId) { alert('Invalid product ID.'); return; }
    if (!onAddToCart)  { alert('Cart not available.'); return; }
    onAddToCart({
      ...currentProduct,
      id:           currentProduct.id,
      apiProductId,
      salePrice:    currentProduct.salePrice ?? null,
      quantity:     1,
    });
  };

  /* ── Loading placeholder ── */
  if (!currentProduct) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
        <p className="mt-3">Loading product details…</p>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     Render
  ════════════════════════════════════════════════════════════════ */
  return (
    <div className="product-detail-page">
      <section className="product-detail-section">
        <div className="container">
          <div className="row">

            {/* ── Left: Image gallery ── */}
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="product-images-container">

                {/* Main large image */}
                <div className="product-main-image mb-3">
                  {imagesLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 420 }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading images…</span>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={selectedImage || currentProduct.image || fallbackImages[0]}
                      alt={currentProduct.name}
                      className="img-fluid main-product-image"
                      style={{
                        width: '100%',
                        objectFit: 'contain',
                        maxHeight: 420,
                        borderRadius: 10,
                        background: '#f8fafc',
                        border: '1px solid #e5e7eb',
                      }}
                      onError={(e) => { e.target.onerror = null; e.target.src = fallbackImages[0]; }}
                    />
                  )}
                </div>

                {/* Thumbnail strip — only when there are 2+ images */}
                {!imagesLoading && productImages.length > 1 && (
                  <>
                    <div className="row g-2">
                      {productImages.map((img) => (
                        <div key={img.id} className="col-3">
                          <div
                            onClick={() => setSelectedImage(img.imageUrl)}
                            onMouseEnter={() => setSelectedImage(img.imageUrl)}
                            style={{
                              cursor: 'pointer',
                              border: selectedImage === img.imageUrl
                                ? '2.5px solid #2563eb'
                                : '2px solid #e5e7eb',
                              borderRadius: 7,
                              overflow: 'hidden',
                              position: 'relative',
                              background: '#f8fafc',
                              transition: 'border-color 0.15s',
                            }}
                          >
                            <img
                              src={img.imageUrl}
                              alt={`${currentProduct.name} — view ${img.displayOrder}`}
                              style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                              onError={(e) => { e.target.onerror = null; e.target.src = fallbackImages[0]; }}
                            />
                            {img.isPrimary && (
                              <span style={{
                                position: 'absolute', bottom: 3, left: 3,
                                background: '#2563eb', color: '#fff',
                                fontSize: '0.58rem', fontWeight: 700,
                                padding: '1px 5px', borderRadius: 4,
                                letterSpacing: '0.02em',
                              }}>
                                Primary
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* e.g. "2 / 4" */}
                    <div className="text-center mt-2">
                      <small className="text-muted">
                        {productImages.findIndex(i => i.imageUrl === selectedImage) + 1} / {productImages.length}
                      </small>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Right: Product info ── */}
            <div className="col-lg-7">
              <h1 className="product-detail-title">{currentProduct.name}</h1>

              <p className="product-category">
                <strong>Category:</strong> {currentProduct.categoryName || currentProduct.category || 'N/A'}
              </p>

              {(hasLicense || hasStateLicense) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0 12px' }}>
                  {hasLicense && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>
                      License Required
                    </span>
                  )}
                  {hasStateLicense && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#faf5ff', border: '1px solid #e9d5ff', color: '#7c3aed', fontSize: '0.78rem', fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>
                      State License Required
                    </span>
                  )}
                </div>
              )}

              <div className="product-price-detail mt-2">
                {currentProduct.salePrice != null && currentProduct.salePrice > 0 ? (
                  <p className="product-detail-price">
                    ${parseFloat(currentProduct.salePrice).toFixed(2)}
                  </p>
                ) : (
                  <div className="product-price-na-detail">
                    <span className="price-na-label">Price: N/A</span>
                    <span className="request-price-label">Request for Price</span>
                  </div>
                )}
              </div>

              {currentProduct.description && currentProduct.description !== 'No description available.' && (
                <div className="product-description mt-3">
                  <p>{currentProduct.description}</p>
                </div>
              )}

              {isInCart ? (
                <CartStatusWidget
                  quantity={cartQuantity}
                  onIncrease={handleIncrease}
                  onDecrease={handleDecrease}
                  onRemove={handleRemove}
                />
              ) : (
                <div className="button-group mt-4">
                  <button className="btn-get-quote me-2" onClick={handleAddToCart}>
                    Add to Cart
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── Related products ── */}
      {!loading && relatedProducts.length > 0 && (
        <section className="more-products-section">
          <div className="container">
            <h2 className="more-products-title">More products</h2>
            <div className="row">
              {relatedProducts.map((product) => (
                <div
                  key={product.id}
                  className="col-lg-3 col-md-4 col-sm-6 mb-4"
                  onClick={() => handleProductClick(product)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="related-product-card">
                    <div className="related-product-image">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="img-fluid"
                        onError={(e) => { e.target.onerror = null; e.target.src = fallbackImages[product.id % fallbackImages.length]; }}
                      />
                    </div>
                    <div className="related-product-info">
                      <h3 className="related-product-name">{product.name}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {loading && (
        <section className="more-products-section">
          <div className="container text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Loading related products…</p>
          </div>
        </section>
      )}

      {!loading && relatedProducts.length === 0 && (
        <section className="more-products-section">
          <div className="container text-center py-5">
            <p className="text-muted">No related products available at the moment.</p>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-box">
            <div className="row align-items-center">
              <div className="col-lg-8 mb-3 mb-lg-0">
                <h3 className="cta-title">Contact us today to learn more about our products.</h3>
              </div>
              <div className="col-lg-4 text-lg-end">
                <button className="btn-cta" onClick={handleGetQuote}>Contact us now</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;